import { createElement } from "react";
import { DynamicIcon } from "lucide-react/dynamic";

/* Helpers shared by the runtime (apply overrides) and the admin "Icons & Labels" editor. */

export const slugify = (s) =>
    String(s || "").toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/* "LayoutDashboard" → "layout-dashboard", "Clock4" → "clock-4" */
export const pascalToKebab = (s) =>
    String(s || "").replace(/([a-z])([A-Z0-9])/g, "$1-$2").replace(/([0-9])([A-Za-z])/g, "$1-$2").toLowerCase();

export const iconNameOf = (Comp) => (Comp && Comp.displayName ? pascalToKebab(Comp.displayName) : "");

const iconCache = new Map();
/* Component rendering a lucide icon by kebab name; cached so React sees a stable type. */
export const isIconUrl = (v) => typeof v === "string" && (v.startsWith("http") || v.startsWith("/"));

export function iconComponent(name) {
    if (!name) return null;
    if (!iconCache.has(name)) {
        const C = isIconUrl(name)
            ? ({ className = "", strokeWidth, absoluteStrokeWidth, ...props }) =>
                createElement("img", { src: name, alt: "", className: `custom-menu-icon object-contain ${className}`.trim(), ...props })
            : ({ className = "", ...props }) => createElement(DynamicIcon, { name, className: `lucide-${name} ${className}`.trim(), ...props });
        C.displayName = `Dyn(${name})`;
        iconCache.set(name, C);
    }
    return iconCache.get(name);
}

export const itemIdOf = (item) => item.uiKey || item.tid || item.id || slugify(item.label || item.title || item.labelKey || item.key || item.to);

const DEFAULTS = { childrenField: "children", labelField: "label", i18nField: null, labels: true, icons: true };

/* Deep-copies a nav structure applying {key: {label, icon}} overrides. Keys: `${prefix}.${id}` and `${prefix}.${id}.${childId}`. */
export function applyOverrides(list, prefix, overrides, options) {
    const opt = { ...DEFAULTS, ...(options || {}) };
    if (!overrides || Object.keys(overrides).length === 0) return list;
    const walk = (items, pre) =>
        items.map((item) => {
            const key = `${pre}.${itemIdOf(item)}`;
            const o = overrides[key];
            let next = item;
            if (o && (o.label || o.icon)) {
                next = { ...item, uiKey: key };
                if (opt.labels && o.label) {
                    next.defaultLabel = item.defaultLabel || item[opt.labelField];
                    next[opt.labelField] = o.label;
                    if (item.labelKey) next.labelKey = undefined;
                    if (opt.i18nField && item[opt.i18nField]) next[opt.i18nField] = undefined;
                }
                if (opt.icons && o.icon) next.icon = iconComponent(o.icon);
            }
            const kids = item[opt.childrenField];
            if (Array.isArray(kids) && kids.length) {
                const walked = walk(kids, key);
                if (walked.some((k, i) => k !== kids[i])) next = next === item ? { ...item, [opt.childrenField]: walked } : { ...next, [opt.childrenField]: walked };
            }
            return next;
        });
    return walk(list, prefix);
}

/* Flattens a structure into rows for the admin editor. */
export function describeStructure(list, prefix, options, resolveLabel) {
    const opt = { ...DEFAULTS, ...(options || {}) };
    const rows = [];
    const walk = (items, pre, depth) => {
        items.forEach((item) => {
            const key = `${pre}.${itemIdOf(item)}`;
            const raw = item[opt.labelField] || (resolveLabel && resolveLabel(item.labelKey || (opt.i18nField && item[opt.i18nField]) || item.key)) || itemIdOf(item);
            rows.push({ key, label: raw, icon: opt.icons ? iconNameOf(item.icon) : "", hasIcon: opt.icons && !!item.icon, labels: opt.labels, depth });
            const kids = item[opt.childrenField];
            if (Array.isArray(kids)) walk(kids, key, depth + 1);
        });
    };
    walk(list, prefix, 0);
    return rows;
}
